import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { navigate } from '@/helpers/router'
import { emitter } from '@/services/mitt'
import { Mixpanel } from '@/analytics/mixpanel'
import { platform } from '@/services/platform'
import type { BillingView } from '@/models/billing'

// Set when we intercept a header navigation click, so we can continue the
// navigation if the user dismisses the popup without acting on it.
let pendingNavigationHref: string | null = null

/**
 * Check if the first URL is 30-min recapture loop specific traffic.
 */
function is30MinRecaptureLoopTraffic(
  firstPage: string | null,
  marketingUrl: string | null,
): boolean {
  function checkEmailTraffic(urlString: string | null): boolean {
    if (!urlString) {
      return false
    }
    // Extract query string part if a full URL is provided
    const queryString = urlString.includes('?')
      ? urlString.split('?')[1]
      : urlString
    const params = new URLSearchParams(queryString)

    return (
      params.get('utm_medium') === 'email' &&
      params.get('utm_source') === 'activecampaign' &&
      params.get('utm_content') === 'day_0'
    )
  }

  return checkEmailTraffic(firstPage) || checkEmailTraffic(marketingUrl)
}

function isPartnerTraffic(
  firstPage: string | null,
  marketingUrl: string | null,
): boolean {
  return Boolean(
    firstPage?.includes('utm_medium=partner') ||
      marketingUrl?.includes('utm_medium=partner'),
  )
}

/**
 * Who should see the hesitation discount offer:
 * - Free users who are not full-access
 * - Not a native app
 * - Not partnership traffic
 * - Not 30-minute recapture-loop traffic
 * - No discount already applied
 */
export async function isHesitationDiscountEligible(
  billing: BillingView,
): Promise<boolean> {
  if (billing.isPaidPlan || billing.hasFullAccess) {
    return false
  }

  if (await platform.isNativeApp()) {
    return false
  }
  const firstPage = storage.getItem(StorageKeys.firstPage)
  const marketingUrl = storage.getItem(StorageKeys.marketingUrl)

  if (isPartnerTraffic(firstPage, marketingUrl)) {
    return false
  }

  if (is30MinRecaptureLoopTraffic(firstPage, marketingUrl)) {
    return false
  }

  if (billing.annualPlan.discount) {
    return false
  }

  return true
}

function hasShownHesitationPopup(): boolean {
  return storage.getItem(StorageKeys.hesitationDiscountShown) === 'true'
}

function removeTriggerListeners(): void {
  document.removeEventListener('mouseout', handleMouseOut)
  document.removeEventListener('click', handleHeaderMenuClick, true)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}

function openHesitationPopup(trigger: string): void {
  storage.setItem(StorageKeys.hesitationDiscountShown, 'true')
  emitter.emit('hesitationPopupOpen')
  Mixpanel.trackHesitationPopupOpen(trigger)
  // The trigger only fires once, so the listeners that could fire it again
  // (mouseout/click/visibilitychange) are no longer needed. We keep
  // listening for the popup's own close event (see handlePopupClose)
  // so we can resume a pending navigation once the user dismisses it.
  removeTriggerListeners()
}

/**
 * If the popup was dismissed without the user acting on it (close button,
 * background click, Esc, swipe-to-close), continue the navigation we
 * intercepted to show it.
 *
 * If there's no pending navigation, the user is staying on the current
 * billing page.
 */
function handlePopupClose(): void {
  if (pendingNavigationHref) {
    void navigate(pendingNavigationHref)
    pendingNavigationHref = null
  }
}

/**
 * Desktop exit intent: the pointer leaves towards the top edge of the page.
 *
 * A null `relatedTarget` is the signal that the pointer left the document
 * entirely.
 * The `clientY <= 0` restricts it to the top edge, the same as in the exit-intent
 * trigger logic used on public pages (see pages-signup-modal/show-modal.helpers.ts).
 */
function handleMouseOut(event: MouseEvent): void {
  if (!event.relatedTarget && event.clientY <= 0) {
    openHesitationPopup('mouseout')
  }
}

/**
 * Intercepts clicks on header navigation links, so the user sees the
 * popup instead of immediately navigating away. Only matches `<a>` elements with
 * an `href` (excludes eg. the header's "Log out" action, which isn't a
 * navigation and has nowhere to resume to).
 *
 * `.navbar-mobile` (the sticky bottom nav shown below the tablet breakpoint,
 * see `AppMobile.vue`) is a separate sibling of `.header` in `Layout.vue`,
 * not nested inside it, so it needs its own clause here.
 */
function handleHeaderMenuClick(event: MouseEvent): void {
  const target = event.target as HTMLElement | null
  const link = target?.closest<HTMLAnchorElement>(
    '.header a[href], .navbar-mobile a[href]',
  )
  if (!link) {
    return
  }

  event.preventDefault()
  pendingNavigationHref = link.pathname + link.search + link.hash
  openHesitationPopup('click')
}

/**
 * Tab/app visibility change. This is the primary trigger on mobile, where
 * `mouseout` doesn't fire, but it also fires on desktop when switching tabs
 * with the keyboard.
 */
function handleVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    openHesitationPopup('visibilitychange')
  }
}

/**
 * Start listening for hesitation signals on the billing page.
 *
 * We don't add listeners if a trigger has already fired for this user.
 */
export function addHesitationTriggerListeners(): void {
  if (hasShownHesitationPopup()) {
    return
  }

  document.addEventListener('mouseout', handleMouseOut)
  // Capture phase, so we intercept the click before a `router-link`'s own
  // handler has a chance to navigate away.
  document.addEventListener('click', handleHeaderMenuClick, true)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  emitter.on('hesitationPopupClose', handlePopupClose)
}

export function removeHesitationTriggerListeners(): void {
  removeTriggerListeners()
  emitter.off('hesitationPopupClose', handlePopupClose)
  pendingNavigationHref = null
}
